/**
 * Hook: useCampaignLogsPaginated
 * ---------------------------------------------------------------------------
 * Consome a RPC `get_campaign_logs` (paginação server-side, filtros server-side)
 * e devolve os logs de execução de uma campanha em formato pronto para
 * `<PaginationFooter>`.
 *
 * Motivação:
 * - Antes o componente `CampaignExecutionLogs` fazia SELECT direto em
 *   `campaign_logs` (até 500 registros) + filtro em JS + Realtime INSERT.
 *   Durante disparos de campanha, essa combinação gerava pico de CPU no
 *   servidor. Agora, com paginação real (30 por página, máx 100), filtros
 *   no banco e polling condicional, o custo cai drasticamente.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

/** Linha de log retornada pela RPC (inclui `total_count` via COUNT OVER()). */
export interface CampaignLogRow {
  id: string;
  campaign_id: string;
  recipient_id: string | null;
  log_level: 'debug' | 'info' | 'warn' | 'error';
  event_type: string;
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  total_count: number;
}

/** Parâmetros do hook. */
export interface UseCampaignLogsPaginatedParams {
  campaignId: string;
  page: number;
  pageSize: number;
  /** Filtro por nível — 'all' equivale a NULL no backend. */
  level?: string;
  /** Filtro por event_type — 'all' equivale a NULL. */
  event?: string;
  /** Busca em `message` (ILIKE). */
  search?: string;
  /** Habilita a query (ex.: apenas quando a aba Logs estiver ativa). */
  enabled?: boolean;
  /** Intervalo de polling em ms. `false`/undefined desativa. */
  refetchInterval?: number | false;
}

/**
 * Hook principal. Retorna itens, contagem total, páginas totais e flags/actions.
 */
export function useCampaignLogsPaginated({
  campaignId,
  page,
  pageSize,
  level,
  event,
  search,
  enabled = true,
  refetchInterval = false,
}: UseCampaignLogsPaginatedParams) {
  const { currentTenant } = useAuth();

  // Normaliza 'all' → null (backend usa NULL para desativar filtro)
  const levelParam = level && level !== 'all' ? level : null;
  const eventParam = event && event !== 'all' ? event : null;
  const searchParam = search && search.trim() !== '' ? search.trim() : null;

  const query = useQuery({
    queryKey: [
      'campaign-logs',
      campaignId,
      currentTenant?.id,
      page,
      pageSize,
      levelParam,
      eventParam,
      searchParam,
    ],
    enabled: enabled && !!campaignId && !!currentTenant?.id,
    // Log é dado que muda; mantemos staleTime curto para permitir refresh manual
    // sem revalidação em cascata, mas ainda respeitar polling explícito.
    staleTime: 5_000,
    refetchInterval: refetchInterval || false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_campaign_logs', {
        p_campaign_id: campaignId,
        p_page: page,
        p_page_size: pageSize,
        p_level: levelParam,
        p_event: eventParam,
        p_search: searchParam,
        p_tenant_hint: currentTenant?.id ?? undefined,
      });
      if (error) {
        logger.error('useCampaignLogsPaginated', `Erro ao buscar logs: ${error.message}`);
        throw error;
      }
      const rows = (data || []) as CampaignLogRow[];
      const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
      return { rows, total };
    },
  });

  const rows = query.data?.rows ?? [];
  const totalCount = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return {
    items: rows,
    totalCount,
    totalPages,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
